import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import { format, startOfWeek, addDays, isSameDay, parseISO, startOfMonth, endOfMonth, endOfWeek, eachDayOfInterval, isSameMonth, addMonths, subMonths } from 'date-fns';
import api, { getImageUrl } from '../api/axios';
import {
  utcToTightNy,
  utcToTightLondon,
  utcLondonZoneAbbr,
  nyClockHour,
  nySlotUtcRange,
  nyDayUtcRange,
  formatNyWeekdayMonthDay,
  utcNyFormatMmmDYyyy,
  utcNyFormatDateTimeEtLabel,
} from '../utils/wallClock';
import { parseEventTitle, searchEventOccurrences, getDateTypeLabel } from '../utils/calendarSearch';
import '../styles/CalendarSearch.css';
import './PublicCalendar.css';

import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const PublicCalendar = () => {
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventPopup, setShowEventPopup] = useState(false);
  const [viewMode, setViewMode] = useState('month'); // 'month' or 'week'
  const [editingEvent, setEditingEvent] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editImage, setEditImage] = useState(null);
  const [cellEvents, setCellEvents] = useState([]);
  const [showCellEventsPopup, setShowCellEventsPopup] = useState(false);
  const [cellDay, setCellDay] = useState(null);
  const [cellHour, setCellHour] = useState(null);
  const isLoggedIn = !!localStorage.getItem('adminToken');

  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchWrapRef = useRef(null);

  /** e.g. "5:30pm" / "5pm" — lowercase am/pm, no space before period */
  const toTightAmPm = (date) => {
    if (!date || isNaN(date.getTime())) return '';
    return format(date, 'h:mma')
      .toLowerCase()
      .replace(/:00(?=[ap]m\b)/, '')
      .replace(/\s+([ap]m)\b/g, '$1');
  };

  /** Week grid hour label in 12h, tight lowercase am/pm */
  const formatHourLabel = (hour24) => {
    if (hour24 === 0) return '12am';
    if (hour24 < 12) return `${hour24}am`;
    if (hour24 === 12) return '12pm';
    return `${hour24 - 12}pm`;
  };

  /** Stored UTC → America/New_York wall clock for tiles/popups */
  const formatEstTimeFromUtcDate = (d) => utcToTightNy(d instanceof Date ? d : parseISO(d));

  // Hide placeholder assets like "no-image" in popups.
  const hasRealImage = (imagePath) => {
    if (!imagePath) return false;
    return !/no[-_ ]?image/i.test(String(imagePath));
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (!searchOpen) return;
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [searchOpen]);

  const fetchEvents = async () => {
    try {
      const response = await api.get('/events');
      // Ensure we always set an array, even if response.data is not an array
      const eventsData = Array.isArray(response.data) ? response.data : [];
      console.log('\n═══════════════════════════════════════════════════════════════════════════════');
      console.log('                    PUBLIC CALENDAR: FETCHING EVENTS - DEBUG');
      console.log('═══════════════════════════════════════════════════════════════════════════════');
      console.log('Total events received:', eventsData.length);
      eventsData.forEach((event, index) => {
        console.log(`\nEvent ${index + 1}:`);
        console.log('  _id:', event._id);
        console.log('  title:', event.title);
        console.log('  firstDate:', event.firstDate);
        console.log('  earlyDate:', event.earlyDate);
        console.log('  publicDate:', event.publicDate);
        if (event.firstDate) {
          const firstDateParsed = parseISO(event.firstDate);
          console.log('  firstDate parsed:', firstDateParsed.toISOString(), 'UTC hours:', firstDateParsed.getUTCHours());
        }
        if (event.earlyDate) {
          const earlyDateParsed = parseISO(event.earlyDate);
          console.log('  earlyDate parsed:', earlyDateParsed.toISOString(), 'UTC hours:', earlyDateParsed.getUTCHours());
        }
        if (event.publicDate) {
          const publicDateParsed = parseISO(event.publicDate);
          console.log('  publicDate parsed:', publicDateParsed.toISOString(), 'UTC hours:', publicDateParsed.getUTCHours());
        }
      });
      console.log('═══════════════════════════════════════════════════════════════════════════════\n');
      setEvents(eventsData);
    } catch (error) {
      console.error('Error fetching events:', error);
      setEvents([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  // Use EST (local time) for week calculation - no UTC conversion
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const hours = Array.from({ length: 24 }, (_, i) => i);

  const getEventSortTimeMs = (ev) => {
    if (!ev) return 0;
    if (ev.date instanceof Date && !isNaN(ev.date.getTime())) return ev.date.getTime();
    if (typeof ev.date === 'string') {
      try {
        const d = parseISO(ev.date);
        if (!isNaN(d.getTime())) return d.getTime();
      } catch (e) { /* ignore */ }
    }
    const fallbacks = [ev.publicDate, ev.firstDate, ev.earlyDate];
    for (const f of fallbacks) {
      if (!f) continue;
      try {
        const d = typeof f === 'string' ? parseISO(f) : f;
        if (d && typeof d.getTime === 'function' && !isNaN(d.getTime())) return d.getTime();
      } catch (e) { /* ignore */ }
    }
    return 0;
  };

  const sortEventsByStartTimeAscending = (list) =>
    [...list].sort((a, b) => {
      const ta = getEventSortTimeMs(a);
      const tb = getEventSortTimeMs(b);
      if (ta !== tb) return ta - tb;
      const sa = String(a?.title || '');
      const sb = String(b?.title || '');
      if (sa !== sb) return sa.localeCompare(sb);
      return String(a?._id || '').localeCompare(String(b?._id || ''));
    });

  const getEventsForDayAndHour = (day, hour) => {
    // Safety check: ensure events is an array before calling filter
    if (!Array.isArray(events)) {
      return [];
    }
    
    const result = [];
    const { start: dayStart, end: dayEnd } = nySlotUtcRange(day, hour);
    
    events.forEach(event => {
      if (!event) return;
      
      // New format: event has eventType and date fields (separate events)
      if (event.eventType && event.date) {
        const eventDate = parseISO(event.date);
        if (eventDate >= dayStart && eventDate < dayEnd) {
          result.push({ ...event, dateType: event.eventType, date: eventDate });
        }
      } else {
        // Legacy format: event has all 3 dates (backward compatibility)
        // Check firstDate
        if (event.firstDate) {
          const firstDate = parseISO(event.firstDate);
          if (firstDate >= dayStart && firstDate < dayEnd) {
            result.push({ ...event, dateType: 'first', date: firstDate });
          }
        }
        
        // Check earlyDate
        if (event.earlyDate) {
          const earlyDate = parseISO(event.earlyDate);
          if (earlyDate >= dayStart && earlyDate < dayEnd) {
            result.push({ ...event, dateType: 'early', date: earlyDate });
          }
        }
        
        // Check publicDate
        if (event.publicDate) {
          const publicDate = parseISO(event.publicDate);
          if (publicDate >= dayStart && publicDate < dayEnd) {
            result.push({ ...event, dateType: 'public', date: publicDate });
          }
        }
      }
    });
    
    return sortEventsByStartTimeAscending(result);
  };

  const navigateWeek = (direction) => {
    const newDate = addDays(currentDate, direction * 7);
    setCurrentDate(newDate);
  };

  const navigateMonth = (direction) => {
    const newDate = direction > 0 ? addMonths(currentDate, 1) : subMonths(currentDate, 1);
    setCurrentDate(newDate);
  };

  const getEventsForDay = (day) => {
    if (!Array.isArray(events)) {
      return [];
    }
    
    const result = [];
    const { start: dayStart, end: dayEnd } = nyDayUtcRange(day);
    
    events.forEach(event => {
      if (!event) return;
      
      if (event.eventType && event.date) {
        const eventDate = parseISO(event.date);
        if (eventDate >= dayStart && eventDate <= dayEnd) {
          result.push({ ...event, dateType: event.eventType, date: eventDate });
        }
      } else {
        if (event.firstDate) {
          const firstDate = parseISO(event.firstDate);
          if (firstDate >= dayStart && firstDate <= dayEnd) {
            result.push({ ...event, dateType: 'first', date: firstDate });
          }
        }
        if (event.earlyDate) {
          const earlyDate = parseISO(event.earlyDate);
          if (earlyDate >= dayStart && earlyDate <= dayEnd) {
            result.push({ ...event, dateType: 'early', date: earlyDate });
          }
        }
        if (event.publicDate) {
          const publicDate = parseISO(event.publicDate);
          if (publicDate >= dayStart && publicDate <= dayEnd) {
            result.push({ ...event, dateType: 'public', date: publicDate });
          }
        }
      }
    });
    
    return sortEventsByStartTimeAscending(result);
  };

  // Helper function to parse description and extract shipmentDate and price
  const parseEventDescription = (description) => {
    if (!description) return { shipmentDate: '', price: '' };
    
    let shipmentDate = '';
    let price = '';
    
    // Extract shipment date
    const shipmentMatch = description.match(/Shipment Date:\s*(.+?)(?:\n|$)/i);
    if (shipmentMatch) {
      shipmentDate = shipmentMatch[1].trim();
    }
    
    // Extract price
    const priceMatch = description.match(/Price:\s*(.+?)(?:\n|$)/i);
    if (priceMatch) {
      price = priceMatch[1].trim();
    }
    
    return { shipmentDate, price };
  };

  // Helper function to group related events by bookName/bookBox
  const getRelatedEvents = (currentEvent) => {
    const { bookName, bookBox } = parseEventTitle(currentEvent.title);
    
    if (!bookName && !bookBox) {
      return { relatedEvents: [], bookName: '', bookBox: '' };
    }
    
    // Find all events with the same bookName and bookBox
    const relatedEvents = events.filter(event => {
      if (!event) return false;
      const parsed = parseEventTitle(event.title);
      return parsed.bookName === bookName && parsed.bookBox === bookBox;
    });
    
    return { relatedEvents, bookName, bookBox };
  };

  // Helper function to find related events by eventType for popup display
  const findRelatedEvents = (currentEvent) => {
    if (!currentEvent || !Array.isArray(events)) {
      return { earlyAccess: null, generalAccess: null, preorderClose: null };
    }

    // Extract bookName and bookBox from current event
    const { bookName, bookBox } = parseEventTitle(currentEvent.title);
    
    if (!bookName && !bookBox) {
      // If we can't parse the title, try to find events with the same _id (for legacy format)
      // or same title
      const sameTitleEvents = events.filter(event => 
        event && event.title === currentEvent.title && event._id !== currentEvent._id
      );
      
      // For legacy format, check if current event has other dates
      if (currentEvent.firstDate || currentEvent.earlyDate || currentEvent.publicDate || currentEvent.preorderCloseDate) {
        return {
          earlyAccess: currentEvent.earlyDate ? currentEvent : null,
          generalAccess: currentEvent.publicDate ? currentEvent : null,
          preorderClose: currentEvent.preorderCloseDate ? currentEvent : null,
        };
      }
      
      return { earlyAccess: null, generalAccess: null, preorderClose: null };
    }
    
    // Find all events with the same bookName and bookBox
    const relatedEvents = events.filter(event => {
      if (!event || event._id === currentEvent._id) return false;
      const parsed = parseEventTitle(event.title);
      return parsed.bookName === bookName && parsed.bookBox === bookBox;
    });
    
    // Find earlyAccess event (eventType === 'early')
    const earlyAccess = relatedEvents.find(event => 
      event.eventType === 'early' || event.dateType === 'early'
    ) || null;
    
    // Find generalAccess event (eventType === 'public')
    const generalAccess = relatedEvents.find(event => 
      event.eventType === 'public' || event.dateType === 'public'
    ) || null;

    const preorderClose = relatedEvents.find(event =>
      event.eventType === 'preorderClose' || event.dateType === 'preorderClose'
    ) || null;
    
    return { earlyAccess, generalAccess, preorderClose };
  };

  // Helper function to get all General Access times with region labels
  const getAllGeneralAccessTimes = (currentEvent, relatedEvents) => {
    const times = [];
    
    // Find all public access events related to the current event
    const { bookName, bookBox } = parseEventTitle(currentEvent.title);
    const allPublicEvents = events.filter(event => {
      if (!event || event._id === currentEvent._id) return false;
      if (event.eventType === 'public' || event.dateType === 'public') {
        if (bookName && bookBox) {
          const parsed = parseEventTitle(event.title);
          return parsed.bookName === bookName && parsed.bookBox === bookBox;
        }
        return event.title === currentEvent.title;
      }
      return false;
    });
    
    // Also check if current event itself is a public access event (for legacy format)
    if ((currentEvent.eventType === 'public' || currentEvent.dateType === 'public') && currentEvent.publicDate) {
      allPublicEvents.push(currentEvent);
    }
    
    // Extract times from each public access event
    allPublicEvents.forEach(event => {
      const eventDate = event.date || event.publicDate;
      if (!eventDate) return;
      
      const dateObj = eventDate instanceof Date ? eventDate : parseISO(eventDate);
      if (!dateObj || isNaN(dateObj.getTime())) return;
      
      // Determine region based on event title or description
      // Use more specific patterns to avoid false matches
      let region = null;
      const title = (event.title || '').toLowerCase();
      const description = (event.description || '').toLowerCase();
      
      // Check for UK/INT patterns (more specific)
      if (title.includes('uk/int') || title.includes('uk/int') || 
          description.includes('uk/int') || description.includes('uk/int') ||
          title.includes('(uk/int)') || description.includes('(uk/int)')) {
        region = 'UK/INT';
      } 
      // Check for US/Canada patterns (more specific)
      else if (title.includes('us/canada') || title.includes('us/can') || 
               description.includes('us/canada') || description.includes('us/can') ||
               title.includes('(us/canada)') || description.includes('(us/canada)') ||
               title.includes('(us/can)') || description.includes('(us/can)')) {
        region = 'US/Canada';
      }
      // Title-only fallback (avoid false UK: shared HTML often has "co.uk" + "international" → not UK/INT)
      else if (title.includes('uk') && (title.includes('int') || title.includes('international'))) {
        region = 'UK/INT';
      } else if ((title.includes('us') || title.includes('united states')) && 
                 (title.includes('canada') || title.includes('can'))) {
        region = 'US/Canada';
      }

      // Fairyloot: General Access uses ~10am BST (early AM ET) for UK/INT and afternoon ET for US/Canada;
      // wall-clock in America/New_York is more reliable than title/shared HTML or "earlier = UK" heuristics.
      const isFl =
        String(bookBox || '')
          .toLowerCase()
          .replace(/[^a-z]/g, '') === 'fairyloot';
      if (isFl) {
        const h = nyClockHour(dateObj);
        if (h < 10) region = 'UK/INT';
        else region = 'US/Canada';
      }
      
      // Always format time in EST
      const timeInfo = formatTimeForRegion(eventDate, 'public');
      if (timeInfo.time) {
        let formattedTime = timeInfo.time;
        let gmtTime = null;
        
        // For UK/INT, add GMT conversion
        let gmtZone = null;
        if (region === 'UK/INT') {
          gmtTime = utcToTightLondon(dateObj);
          gmtZone = utcLondonZoneAbbr(dateObj);
        }
        
        times.push({
          time: formattedTime,
          timezone: 'ET',
          label: 'US Eastern time',
          region: region,
          gmtTime: gmtTime,
          gmtZone,
          _sortTs: dateObj.getTime()
        });
      }
    });
    
    // If no times found but we have generalAccessInfo, use that
    if (times.length === 0 && relatedEvents.generalAccess) {
      const generalAccessDate = relatedEvents.generalAccess.date || relatedEvents.generalAccess.publicDate;
      if (generalAccessDate) {
        const timeInfo = formatTimeForRegion(generalAccessDate, 'public');
        if (timeInfo.time) {
          const d = generalAccessDate instanceof Date ? generalAccessDate : parseISO(generalAccessDate);
          const ts = d && !isNaN(d.getTime()) ? d.getTime() : 0;
          times.push({
            time: timeInfo.time,
            timezone: 'ET',
            label: 'US Eastern time',
            region: null,
            gmtTime: null,
            gmtZone: null,
            _sortTs: ts
          });
        }
      }
    }

    const dedup = new Map();
    for (const t of times) {
      const r = t.region || 'Worldwide';
      const key = `${t._sortTs}|${r}|${t.time}`;
      if (!dedup.has(key)) dedup.set(key, t);
    }
    const out = [...dedup.values()];
    return out.map(({ _sortTs, ...rest }) => rest);
  };

  // Helper function to extract date/time info from related events
  const extractEventDetails = (currentEvent) => {
    const { relatedEvents, bookName, bookBox } = getRelatedEvents(currentEvent);
    const { shipmentDate, price } = parseEventDescription(currentEvent.description || '');
    
    // Initialize all fields with N/A
    const details = {
      bookName: bookName || 'N/A',
      bookBox: bookBox || 'N/A',
      bookPrice: price || 'N/A',
      shipmentDate: shipmentDate || 'N/A',
      openPreorderDate: 'N/A',
      openPreorderTime: 'N/A',
      preorderCloseDate: 'N/A',
      preorderCloseTime: 'N/A',
      membersOnlyDate: 'N/A',
      membersOnlyTime: 'N/A',
      earlyAccessDate: 'N/A',
      earlyAccessUKINT: 'N/A',
      earlyAccessUSCanada: 'N/A',
      activeSubscriberDate: 'N/A',
      activeSubscriberUKINT: 'N/A',
      activeSubscriberUSCanada: 'N/A',
      publicAccessDate: 'N/A',
      publicAccessUKINT: 'N/A',
      publicAccessUSCanada: 'N/A',
      author: currentEvent.author || 'N/A',
      linkToPost: currentEvent.hyperlink || 'N/A'
    };
    
    // Extract date/time from related events
    relatedEvents.forEach(event => {
      if (!event.date) return;
      
      const eventDate = event.date instanceof Date ? event.date : parseISO(event.date);
      const dateStr = utcNyFormatMmmDYyyy(eventDate);
      const timeStr = utcToTightNy(eventDate);
      
      switch (event.eventType) {
        case 'openPreorder':
          details.openPreorderDate = dateStr;
          details.openPreorderTime = timeStr;
          break;
        case 'preorderClose':
          details.preorderCloseDate = dateStr;
          details.preorderCloseTime = timeStr;
          break;
        case 'membersOnly':
          details.membersOnlyDate = dateStr;
          details.membersOnlyTime = timeStr;
          break;
        case 'first':
          details.earlyAccessDate = dateStr;
          // Try to determine if it's UK/INT or US/Canada based on time
          // This is a heuristic - UK/INT is typically earlier in the day
          // Use EST (local time) - no UTC conversion
          if (nyClockHour(eventDate) < 12) {
            if (details.earlyAccessUKINT === 'N/A') {
              details.earlyAccessUKINT = timeStr;
            }
          } else {
            if (details.earlyAccessUSCanada === 'N/A') {
              details.earlyAccessUSCanada = timeStr;
            }
          }
          break;
        case 'early':
          details.activeSubscriberDate = dateStr;
          // Similar heuristic for active subscriber times
          // Use EST (local time) - no UTC conversion
          if (nyClockHour(eventDate) < 12) {
            if (details.activeSubscriberUKINT === 'N/A') {
              details.activeSubscriberUKINT = timeStr;
            }
          } else {
            if (details.activeSubscriberUSCanada === 'N/A') {
              details.activeSubscriberUSCanada = timeStr;
            }
          }
          break;
        case 'public':
          details.publicAccessDate = dateStr;
          // Similar heuristic for public access times
          // Use EST (local time) - no UTC conversion
          if (nyClockHour(eventDate) < 12) {
            if (details.publicAccessUKINT === 'N/A') {
              details.publicAccessUKINT = timeStr;
            }
          } else {
            if (details.publicAccessUSCanada === 'N/A') {
              details.publicAccessUSCanada = timeStr;
            }
          }
          break;
      }
    });
    
    return details;
  };

  // Helper function to format date/time for popup
  const formatDateTime = (event) => {
    if (!event) return '';
    let eventDate = null;
    
    // Try to get the date from various sources
    if (event.date) {
      eventDate = event.date instanceof Date ? event.date : parseISO(event.date);
    } else if (event.dateType === 'first' && event.firstDate) {
      eventDate = event.firstDate instanceof Date ? event.firstDate : parseISO(event.firstDate);
    } else if (event.dateType === 'early' && event.earlyDate) {
      eventDate = event.earlyDate instanceof Date ? event.earlyDate : parseISO(event.earlyDate);
    } else if (event.dateType === 'public' && event.publicDate) {
      eventDate = event.publicDate instanceof Date ? event.publicDate : parseISO(event.publicDate);
    } else if (event.firstDate) {
      eventDate = event.firstDate instanceof Date ? event.firstDate : parseISO(event.firstDate);
    } else if (event.earlyDate) {
      eventDate = event.earlyDate instanceof Date ? event.earlyDate : parseISO(event.earlyDate);
    } else if (event.publicDate) {
      eventDate = event.publicDate instanceof Date ? event.publicDate : parseISO(event.publicDate);
    }
    
    if (!eventDate || isNaN(eventDate.getTime())) return '';
    
    const endDate = DateTime.fromJSDate(eventDate, { zone: 'utc' }).plus({ hours: 1 }).toJSDate();
    const dayName = formatNyWeekdayMonthDay(eventDate);
    const startTime = utcToTightNy(eventDate);
    const endTime = utcToTightNy(endDate);
    return (
      <>
        <span>{dayName}</span>
        <span className="yuNITc">·</span>
        <span>{startTime} – {endTime}</span>
      </>
    );
  };

  // Helper function to format time for a specific region (returns time and timezone label)
  // dateInput is in UTC/GMT, we convert to the appropriate timezone based on region
  const formatTimeForRegion = (dateInput, regionType) => {
    if (!dateInput) return { time: null, timezone: '', label: '' };
    
    let eventDate = null;
    
    if (dateInput instanceof Date) {
      eventDate = new Date(dateInput);
    } else if (typeof dateInput === 'string') {
      eventDate = parseISO(dateInput);
    } else {
      return { time: null, timezone: '', label: '' };
    }
    
    if (!eventDate || isNaN(eventDate.getTime())) return { time: null, timezone: '', label: '' };
    
    if (regionType === 'first' || regionType === 'UK/INT') {
      const abbr = utcLondonZoneAbbr(eventDate);
      return {
        time: utcToTightLondon(eventDate),
        timezone: abbr,
        label: `UK (${abbr})`,
      };
    }
    if (regionType === 'early' || regionType === 'US/Canada' || regionType === 'public' || regionType === 'Worldwide') {
      return {
        time: utcToTightNy(eventDate),
        timezone: 'ET',
        label: 'US Eastern time',
      };
    }
    const abbr = utcLondonZoneAbbr(eventDate);
    return {
      time: utcToTightLondon(eventDate),
      timezone: abbr,
      label: `UK (${abbr})`,
    };
  };

  // Helper function to get ordinal suffix (st, nd, rd, th)
  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  // Helper function to format date with ordinal (e.g., "Jan 15th")
  const formatDateWithOrdinal = (dateInput) => {
    if (!dateInput) return null;
    
    let eventDate = null;
    
    // Handle different input types
    if (dateInput instanceof Date) {
      eventDate = dateInput;
    } else if (typeof dateInput === 'string') {
      eventDate = parseISO(dateInput);
    } else {
      return null;
    }
    
    if (!eventDate || isNaN(eventDate.getTime())) return null;
    
    const dt = DateTime.fromJSDate(eventDate, { zone: 'utc' }).setZone('America/New_York');
    const month = dt.toFormat('MMM');
    const day = dt.day;
    const ordinal = getOrdinalSuffix(day);
    
    return `${month} ${day}${ordinal}`;
  };

  // Helper function to format time as "8am" or "8:30am" (lowercase, no leading zero for hours)
  const formatTimeSimple = (dateInput) => {
    if (!dateInput) return null;
    const eventDate =
      dateInput instanceof Date ? dateInput : typeof dateInput === 'string' ? parseISO(dateInput) : null;
    if (!eventDate || isNaN(eventDate.getTime())) return null;
    return utcToTightNy(eventDate) || null;
  };

  const inferEarlySubscriberRegionFromEvent = (event) => {
    const d = (event.description || '').toLowerCase();
    const t = (event.title || '').toLowerCase();
    const hasUkLine = d.includes('uk/int active subscribers');
    const hasUsLine = d.includes('us/canada active subscribers');
    if (hasUkLine && hasUsLine) return null;
    if (hasUsLine) return 'US/Canada';
    if (hasUkLine) return 'UK/INT';
    if (t.includes('us/canada') || t.includes('(us/can')) return 'US/Canada';
    if (t.includes('uk/int') || t.includes('(uk/int)')) return 'UK/INT';
    return null;
  };

  const isFairylootBookBox = (bookBox) =>
    String(bookBox || '')
      .toLowerCase()
      .replace(/[^a-z]/g, '') === 'fairyloot';

  /** After "ET", UK/INT or US/Canada (Fairyloot only). No other trailing text. */
  const fairylootEtRegionalSuffix = (timeInfo, bookBox) => {
    if (!isFairylootBookBox(bookBox)) return '';
    if (timeInfo.region === 'UK/INT') return ' UK/INT';
    if (timeInfo.region === 'US/Canada') return ' US/Canada';
    return '';
  };

  /** With Fairyloot, hide GMT paren; regional label is enough on the time line. */
  const showUkIntGmtParens = (timeInfo, bookBox) =>
    timeInfo.region === 'UK/INT' &&
    timeInfo.gmtTime &&
    !isFairylootBookBox(bookBox);

  const inferFirstAccessRegionFromEvent = (event) => {
    const d = (event.description || '').toLowerCase();
    const t = (event.title || '').toLowerCase();
    if (d.includes('uk/int') && (d.includes('customers who purchased') || d.includes('previous'))) return 'UK/INT';
    if (d.includes('us/canada') && (d.includes('customers who purchased') || d.includes('previous'))) return 'US/Canada';
    if (d.includes('first access') && d.includes('uk')) return 'UK/INT';
    if (d.includes('first access') && d.includes('us') && d.includes('canada')) return 'US/Canada';
    if (t.includes('us/canada') || t.includes('(us/can')) return 'US/Canada';
    if (t.includes('uk/int') || t.includes('(uk/int)')) return 'UK/INT';
    return null;
  };

  const getAllFirstAccessTimes = (currentEvent) => {
    if (!currentEvent || !Array.isArray(events)) return [];
    const { bookName, bookBox } = parseEventTitle(currentEvent.title);
    const allFirstEvents = events.filter((event) => {
      if (!event || event._id === currentEvent._id) return false;
      if (event.eventType !== 'first' && event.dateType !== 'first') return false;
      if (bookName && bookBox) {
        const parsed = parseEventTitle(event.title);
        return parsed.bookName === bookName && parsed.bookBox === bookBox;
      }
      return event.title === currentEvent.title;
    });
    if (currentEvent.eventType === 'first' || currentEvent.dateType === 'first') {
      allFirstEvents.push(currentEvent);
    }
    const times = [];
    allFirstEvents.forEach((event) => {
      const eventDate = event.date || event.firstDate;
      if (!eventDate) return;
      const dateObj = eventDate instanceof Date ? eventDate : parseISO(eventDate);
      if (!dateObj || isNaN(dateObj.getTime())) return;
      let region = inferFirstAccessRegionFromEvent(event);
      if (!region) {
        region = nyClockHour(dateObj) < 12 ? 'UK/INT' : 'US/Canada';
      }
      const estTime = formatTimeSimple(dateObj);
      if (!estTime) return;
      const gmtTime = region === 'UK/INT' ? utcToTightLondon(dateObj) : null;
      const gmtZone = region === 'UK/INT' ? utcLondonZoneAbbr(dateObj) : null;
      times.push({
        time: estTime,
        gmtTime,
        gmtZone,
        region,
        _sortTs: dateObj.getTime(),
      });
    });
    const dedup = new Map();
    for (const t of times) {
      const key = `${t._sortTs}|${t.region}|${t.time}`;
      if (!dedup.has(key)) dedup.set(key, t);
    }
    const uniq = [...dedup.values()].map(({ _sortTs, ...rest }) => rest);
    uniq.sort((a, b) => {
      const parseTime = (timeStr) => {
        const match = timeStr.match(/(\d+)(?::(\d+))?(am|pm)/i);
        if (!match) return 0;
        let hours = parseInt(match[1], 10);
        const period = match[3].toLowerCase();
        if (period === 'pm' && hours !== 12) hours += 12;
        if (period === 'am' && hours === 12) hours = 0;
        return hours;
      };
      return parseTime(a.time) - parseTime(b.time);
    });
    return uniq;
  };

  const getAllEarlyAccessTimes = (currentEvent) => {
    if (!currentEvent || !Array.isArray(events)) return [];
    const { bookName, bookBox } = parseEventTitle(currentEvent.title);
    const allEarlyEvents = events.filter((event) => {
      if (!event || event._id === currentEvent._id) return false;
      if (event.eventType !== 'early' && event.dateType !== 'early') return false;
      if (bookName && bookBox) {
        const parsed = parseEventTitle(event.title);
        return parsed.bookName === bookName && parsed.bookBox === bookBox;
      }
      return event.title === currentEvent.title;
    });
    if (currentEvent.eventType === 'early' || currentEvent.dateType === 'early') {
      allEarlyEvents.push(currentEvent);
    }
    const times = [];
    allEarlyEvents.forEach((event) => {
      const eventDate = event.date || event.earlyDate;
      if (!eventDate) return;
      const dateObj = eventDate instanceof Date ? eventDate : parseISO(eventDate);
      if (!dateObj || isNaN(dateObj.getTime())) return;
      let region = inferEarlySubscriberRegionFromEvent(event);
      if (!region) {
        region = nyClockHour(dateObj) < 12 ? 'UK/INT' : 'US/Canada';
      }
      const estTime = formatTimeSimple(dateObj);
      if (!estTime) return;
      const gmtTime = region === 'UK/INT' ? utcToTightLondon(dateObj) : null;
      const gmtZone = region === 'UK/INT' ? utcLondonZoneAbbr(dateObj) : null;
      times.push({
        time: estTime,
        gmtTime,
        gmtZone,
        region,
        _sortTs: dateObj.getTime(),
      });
    });
    const dedup = new Map();
    for (const t of times) {
      const key = `${t._sortTs}|${t.region}|${t.time}`;
      if (!dedup.has(key)) dedup.set(key, t);
    }
    const uniq = [...dedup.values()].map(({ _sortTs, ...rest }) => rest);
    uniq.sort((a, b) => {
      const parseTime = (timeStr) => {
        const match = timeStr.match(/(\d+)(?::(\d+))?(am|pm)/i);
        if (!match) return 0;
        let hours = parseInt(match[1], 10);
        const period = match[3].toLowerCase();
        if (period === 'pm' && hours !== 12) hours += 12;
        if (period === 'am' && hours === 12) hours = 0;
        return hours;
      };
      return parseTime(a.time) - parseTime(b.time);
    });
    return uniq;
  };

  // Helper function to format event date for popup (returns object with dayName, startTime, endTime)
  // Uses EST (local time) - no timezone conversion
  const formatEventDate = (dateInput) => {
    if (!dateInput) return null;
    
    let eventDate = null;
    
    // Handle different input types
    if (dateInput instanceof Date) {
      eventDate = dateInput;
    } else if (typeof dateInput === 'string') {
      eventDate = parseISO(dateInput);
    } else {
      return null;
    }
    
    if (!eventDate || isNaN(eventDate.getTime())) return null;
    
    const endDate = DateTime.fromJSDate(eventDate, { zone: 'utc' }).plus({ hours: 1 }).toJSDate();
    const dayName = formatNyWeekdayMonthDay(eventDate);
    const startTime = utcToTightNy(eventDate);
    const endTime = utcToTightNy(endDate);
    
    return {
      dayName,
      startTime,
      endTime
    };
  };

  // Helper function to format time with GMT conversion for UK/INT times
  const formatTimeWithGMT = (dateInput, isUKINT) => {
    if (!dateInput || !isUKINT) {
      return null;
    }
    
    let eventDate = null;
    
    // Handle different input types
    if (dateInput instanceof Date) {
      eventDate = dateInput;
    } else if (typeof dateInput === 'string') {
      eventDate = parseISO(dateInput);
    } else {
      return null;
    }
    
    if (!eventDate || isNaN(eventDate.getTime())) return null;
    return utcToTightLondon(eventDate);
  };

  // Helper function to determine region label based on time comparison
  // Earlier times = UK/INT, Later times = US/Canada
  const getRegionLabelByTime = (currentDate, compareDates = []) => {
    if (!currentDate) return '';
    
    let currentEventDate = null;
    if (currentDate instanceof Date) {
      currentEventDate = currentDate;
    } else if (typeof currentDate === 'string') {
      currentEventDate = parseISO(currentDate);
    } else {
      return '';
    }
    
    if (!currentEventDate || isNaN(currentEventDate.getTime())) return '';
    
    const currentTime = nyClockHour(currentEventDate);
    
    // If we have comparison dates, compare with them
    if (compareDates.length > 0) {
      const compareTimes = compareDates
        .map(date => {
          let eventDate = null;
          if (date instanceof Date) {
            eventDate = date;
          } else if (typeof date === 'string') {
            eventDate = parseISO(date);
          }
          if (!eventDate || isNaN(eventDate.getTime())) return null;
          return nyClockHour(eventDate);
        })
        .filter(time => time !== null);
      
      if (compareTimes.length > 0) {
        const isEarlier = compareTimes.every(compareTime => currentTime < compareTime);
        return isEarlier ? ' (UK/INT)' : ' (US/Canada)';
      }
    }
    
    // If no comparison dates, use noon EST as threshold (before noon = UK/INT, after = US/Canada)
    return currentTime < 12 ? ' (UK/INT)' : ' (US/Canada)';
  };

  // Helper function to extract shipment date from description
  const extractShipmentDate = (description) => {
    if (!description) return null;
    // Try to find "Shipment Date:" or "Expected to ship:" or similar patterns
    const patterns = [
      /(?:Shipment Date|Expected to ship|Estimate to ship|Shipping Date|Estimated to ship):\s*(.+?)(?:\n|<br|$)/i,
      /(?:Ship|Shipping|Estimate).*?:\s*(.+?)(?:\n|<br|$)/i,
    ];
    
    for (const pattern of patterns) {
      const match = description.match(pattern);
      if (match && match[1]) {
        // Remove HTML tags from the extracted value
        let value = match[1].trim();
        value = value.replace(/<[^>]*>/g, ''); // Remove all HTML tags
        value = value.replace(/&nbsp;/g, ' '); // Replace &nbsp; with space
        return value.trim();
      }
    }
    return null;
  };

  // Helper function to extract link URL from description
  const extractLinkFromDescription = (description) => {
    if (!description) return null;
    // Try to find any anchor tag with href (legacy posts may not use literal "LINK" text).
    const linkPattern = /<a[^>]*href=["']([^"']+)["'][^>]*>[\s\S]*?<\/a>/i;
    const match = description.match(linkPattern);
    if (match && match[1]) {
      return match[1].trim();
    }
    return null;
  };

  // Helper function to format description for popup - formats with each item on new line
  const formatDescription = (description, event = null) => {
    if (!description || description.trim() === '') {
      // If no description but we have shipment date, show it
      const shipmentDate = extractShipmentDate(event?.description || '');
      if (shipmentDate) {
        return `Estimate to Ship: <strong>${shipmentDate}</strong>`;
      }
      return '';
    }
    
    // Remove HTML tags first
    let formatted = description.replace(/<[^>]*>/g, '');
    
    // Decode HTML entities
    const textarea = document.createElement('textarea');
    textarea.innerHTML = formatted;
    formatted = textarea.value;
    
    // Extract shipment date
    const shipmentDate = extractShipmentDate(formatted);
    
    // Remove shipment date line from description if it exists
    formatted = formatted.replace(/(?:Shipment Date|Expected to ship|Estimate to ship|Shipping Date):\s*.+?(?:\n|$)/gi, '');
    
    // Timezone explanations
    const timezoneMap = {
      'GMT': 'GMT (Greenwich Mean Time)',
      'UTC': 'UTC (Coordinated Universal Time)',
      'EST': 'EST (Eastern Standard Time)',
      'EDT': 'EDT (Eastern Daylight Time)',
      'PST': 'PST (Pacific Standard Time)',
      'PDT': 'PDT (Pacific Daylight Time)',
      'CST': 'CST (Central Standard Time)',
      'CDT': 'CDT (Central Daylight Time)',
    };
    
    // Region detection patterns
    const regionPatterns = {
      'UKINT': /(?:UK|INT|UK\/INT|United Kingdom|International)/gi,
      'USCAN': /(?:US|Canada|US\/Canada|US\/CAN|United States)/gi,
      'Worldwide': /(?:Worldwide|World|Global)/gi,
    };
    
    // Split description into lines and process each
    const lines = formatted.split(/\n+/).filter(line => line.trim());
    const formattedLines = [];
    
    lines.forEach(line => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return;
      
      // Check if line contains date/time information
      const dateTimePattern = /(\b(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+\d{1,2}(?:st|nd|rd|th)?(?:,\s+\d{4})?|\d{1,2}\/\d{1,2}\/\d{2,4}|\d{1,2}-\d{1,2}-\d{2,4})\s*[:\-]?\s*(\d{1,2}:\d{2}\s*(?:AM|PM|am|pm)?|\d{1,2}\s*(?:AM|PM|am|pm))?\s*([A-Z]{2,4})?/gi;
      const dateTimeMatch = trimmedLine.match(dateTimePattern);
      
      if (dateTimeMatch) {
        // Extract date, time, and timezone
        const fullMatch = dateTimeMatch[0];
        const dateMatch = trimmedLine.match(/\b(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+\d{1,2}(?:st|nd|rd|th)?(?:,\s+\d{4})?|\d{1,2}\/\d{1,2}\/\d{2,4}|\d{1,2}-\d{1,2}-\d{2,4}/i);
        const timeMatch = trimmedLine.match(/\d{1,2}:\d{2}\s*(?:AM|PM|am|pm)?|\d{1,2}\s*(?:AM|PM|am|pm)/i);
        const tzMatch = trimmedLine.match(/\b(GMT|UTC|EST|EDT|PST|PDT|CST|CDT)\b/i);
        
        // Detect region
        let region = '';
        if (regionPatterns.UKINT.test(trimmedLine)) {
          region = ' (UK/INT)';
        } else if (regionPatterns.USCAN.test(trimmedLine)) {
          region = ' (US/Canada)';
        } else if (regionPatterns.Worldwide.test(trimmedLine)) {
          region = ' (Worldwide)';
        }
        
        // Build formatted line
        let formattedLine = '';
        if (dateMatch) {
          formattedLine += `<strong>Date:</strong> ${dateMatch[0]}<br>`;
        }
        if (timeMatch) {
          formattedLine += `<strong>Time:</strong> ${timeMatch[0]}`;
          if (tzMatch) {
            const tz = tzMatch[1].toUpperCase();
            formattedLine += ` ${timezoneMap[tz] || tz}`;
          } else {
            formattedLine += ' GMT';
          }
          formattedLine += `${region}<br>`;
        } else if (dateMatch) {
          formattedLine += `<br>`;
        }
        
        // Add any remaining text from the line
        const remainingText = trimmedLine.replace(fullMatch, '').trim();
        if (remainingText && !regionPatterns.UKINT.test(remainingText) && !regionPatterns.USCAN.test(remainingText) && !regionPatterns.Worldwide.test(remainingText)) {
          formattedLine += remainingText + '<br>';
        }
        
        formattedLines.push(formattedLine);
      } else {
        // Regular line - check for timezone mentions
        let processedLine = trimmedLine;
        
        // Replace timezone abbreviations with explanations
        Object.keys(timezoneMap).forEach(tz => {
          const regex = new RegExp(`\\b${tz}\\b`, 'gi');
          processedLine = processedLine.replace(regex, timezoneMap[tz]);
        });
        
        // Detect region in regular lines
        let region = '';
        if (regionPatterns.UKINT.test(processedLine)) {
          region = ' (UK/INT)';
        } else if (regionPatterns.USCAN.test(processedLine)) {
          region = ' (US/Canada)';
        } else if (regionPatterns.Worldwide.test(processedLine)) {
          region = ' (Worldwide)';
        }
        
        formattedLines.push(processedLine + region + '<br>');
      }
    });
    
    let result = formattedLines.join('');
    
    // Add "Estimate to Ship" at the end if we found a shipment date
    if (shipmentDate) {
      result = result.trim();
      if (result) {
        result += '<br><br>Estimate to Ship: <strong>' + shipmentDate + '</strong>';
      } else {
        result = 'Estimate to Ship: <strong>' + shipmentDate + '</strong>';
      }
    }
    
    return result;
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setShowEventPopup(true);
  };

  const runCalendarSearch = () => {
    const t = searchInput.trim();
    if (!t) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }
    setSearchResults(searchEventOccurrences(events, t));
    setSearchOpen(true);
  };

  const handleSearchResultPick = (row) => {
    const d = row.date;
    setCurrentDate(new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0));
    setViewMode('month');
    setSearchInput('');
    setSearchResults([]);
    setSearchOpen(false);
    const dateTypeLabel = getDateTypeLabel(row.dateType);
    handleEventClick({
      ...row.event,
      dateType: row.dateType,
      dateTypeLabel,
      date: d,
    });
  };

  const handleClosePopup = () => {
    setShowEventPopup(false);
    setSelectedEvent(null);
  };

  const handleCellEventsClick = (day, hour = null) => {
    let eventsForCell = [];
    if (hour !== null) {
      // Week view: get events for specific day and hour
      eventsForCell = getEventsForDayAndHour(day, hour);
    } else {
      // Month view: get events for entire day
      eventsForCell = getEventsForDay(day);
    }
    setCellEvents(eventsForCell);
    setCellDay(day);
    setCellHour(hour);
    setShowCellEventsPopup(true);
  };

  const handleCloseCellEventsPopup = () => {
    setShowCellEventsPopup(false);
    setCellEvents([]);
    setCellDay(null);
    setCellHour(null);
  };

  // Helper function to get color from bookBox
  const getBookBoxColor = (bookBox) => {
    if (!bookBox) return null;
    
    // Normalize book box name: lowercase, trim, remove extra spaces (case-insensitive comparison)
    const normalized = String(bookBox).toLowerCase().trim().replace(/\s+/g, ' ');
    
    // Comprehensive BookBox to color mapping
    const bookBoxColorMap = {
      'acrylipics': '#EFBF04',
      'all of the above book box': '#FF8559',
      'aota': '#FF8559',
      'allurial': '#000000',
      'aurora': '#E0218A',
      'autumn midnights': '#8F9F9A',
      'baddies': '#FF00FF',
      'bad women books': '#E89EB8',
      'merch with indies': '#722F37',
      'bewitched pages': '#06402B',
      'belle book box': '#8E5A6A',
      'beyond the pages': '#2F5F55',
      'book in a box': '#cf597d',
      'bookish and spice': '#8E5A6A',
      'books for days crate': '#878686',
      'books for days crate (bfdc)': '#878686',
      'bfdc': '#878686',
      'books for days': '#878686',
      'c2c': '#8897a6',
      'coveted cover': '#78C7C7',
      'dainty': '#ffe3e3',
      'dark & disturbed': '#323232',
      'dark and disturbed': '#323232',
      'blackout romance box': '#1e1e1e',
      'motley chronicles': '#510466',
      'dark and nerdy bookbox': '#0494b8',
      'dark & quirky': '#eaeaea',
      'dark and quirky': '#eaeaea',
      'endless pages': '#323232',
      'eternal embers': '#6b6570',
      'euphoric lit': '#282828',
      'everheart': '#c486b5',
      'fae crate': '#7d1799',
      'fairyloot': '#483773',
      'fated romance': '#6b91e3',
      'final score bookbox': '#1F405F',
      'foxglove fantasy fiction': '#F1C3B7',
      'fox and wit': '#E3BE3C',
      'haunted hearts': '#740503',
      'hello lovely box & boutique': '#7DBFE6',
      'hello lovely box and boutique': '#7DBFE6',
      'hidden secrets bookstore': '#3B3B3B',
      'illumicrate': '#FDDD57',
      'afterlight': '#FFE8A3',
      'starbright': '#FFEE58',
      'evernight': '#E3B400',
      'mindsight': '#FFC94A',
      'lavish library': '#C9C5B9',
      'la petite mort book box': '#8C0000',
      'lilac library\'s': '#C3B4D0',
      'lilac library': '#C3B4D0',
      'lit haven booktique': '#D2D2D1',
      'litjoy': '#454545',
      'little wicked book box': '#BFE9D6',
      'luna & lore': '#196E92',
      'luna and lore': '#196E92',
      'lunarya': '#9560A1',
      'midnight whispers': '#1B1030',
      'roses & thorns': '#B28AAA',
      'roses and thorns': '#B28AAA',
      'phoenix book box': '#CC4A00',
      'moonbowbookclub': '#3E7ACB',
      'moonlight': '#4C235E',
      'mystic box': '#390706',
      'nocturnal ink book co': '#b08d57',
      'obsidian descension book box': '#77274D',
      'owlcrate': '#1D1432',
      'page and wick': '#00273A',
      'pretty gal x crates': '#bf9000',
      'pretty gal x crates (pxc)': '#bf9000',
      'pxc': '#bf9000',
      'red flags & roses': '#6C0200',
      'red flags and roses': '#6C0200',
      'read in peace book box': '#C7FF00',
      'rip': '#C7FF00',
      'renegade romance': '#E8567B',
      'romance cartel': '#D57A93',
      'romance era book box': '#A3386C',
      'romance reveal book box': '#F50C72',
      'shades of desire books & co.': '#2C2D31',
      'shades of desire books and co.': '#2C2D31',
      'sinful obsessions': '#332828',
      'smut & sip box': '#870022',
      'smut and sip box': '#870022',
      'sonny book box': '#704745',
      'spiced book box': '#EEE0D6',
      'the arcane society': '#B9A191',
      'the book cove': '#6E438A',
      'the bookaholic book box': '#D7A75C',
      'the bookish box': '#0B2C04',
      'bookish box': '#0B2C04',
      'the bound collector': '#9B6B4E',
      'the brightside candles': '#500011',
      'the broken binding': '#0B6F36',
      'the butterfly book club': '#F4CCCD',
      'the ink pages': '#1A3A2C',
      'the last chapter bookbox': '#FAF5F8',
      'the locked library': '#FC038A',
      'the lovely story society': '#7C6A66',
      'the midnight bookshelf': '#9FA9F8',
      'the pretty little words': '#CCAC00',
      'tplw': '#CCAC00',
      'the quill & quiver': '#7F451D',
      'the quill and quiver': '#7F451D',
      'twisted fiction book box': '#BF00FF',
      'twisted retreat': '#595959',
      'unplugged book box': '#63b999',
      'wolfsbane fantasy fiction': '#1D3948',
    };
    
    // Check exact match first
    if (bookBoxColorMap[normalized]) {
      return bookBoxColorMap[normalized];
    }
    
    // Check for partial matches (case-insensitive, ignoring special characters)
    const normalizedForMatch = normalized.replace(/[&'.,]/g, '').replace(/\s+/g, ' ').toLowerCase();
    for (const [key, color] of Object.entries(bookBoxColorMap)) {
      const keyNormalized = String(key).replace(/[&'.,]/g, '').replace(/\s+/g, ' ').toLowerCase();
      // Check if normalized input contains the key or vice versa (case-insensitive comparison)
      if (normalizedForMatch === keyNormalized || 
          normalizedForMatch.includes(keyNormalized) || 
          keyNormalized.includes(normalizedForMatch)) {
        return color;
      }
    }
    
    // Default: generate a color based on bookBox hash
    let hash = 0;
    for (let i = 0; i < normalized.length; i++) {
      hash = normalized.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Generate a color from the hash
    const hue = Math.abs(hash % 360);
    return `hsl(${hue}, 65%, 50%)`;
  };

  // Helper function to get event color
  const getEventColor = (event) => {
    // If event has a direct color property, use it
    if (event?.color) {
      return event.color;
    }
    
    // Try to get color from bookBox
    const { bookBox } = parseEventTitle(event?.title || '');
    if (bookBox) {
      const bookBoxColor = getBookBoxColor(bookBox);
      if (bookBoxColor) {
        return bookBoxColor;
      }
    }
    
    // Otherwise, map dateType to color
    const dateType = event?.dateType || event?.eventType || 'public';
    const colorMap = {
      'first': '#c5221f',
      'early': '#ea8600',
      'public': '#1a73e8',
      'openPreorder': '#34a853',
      'preorderClose': '#9aa0a6',
      'membersOnly': '#9c27b0'
    };
    
    return colorMap[dateType] || colorMap['public'];
  };


  // Helper function to convert ISO date string to datetime-local format
  // Uses EST (local time) - no UTC conversion
  const formatDateTimeLocal = (isoString) => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return '';
      // Use local time methods (EST) - no UTC conversion
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (error) {
      console.error('Error formatting date:', isoString, error);
      return '';
    }
  };

  const handleEdit = (event) => {
    if (!event) return;
    handleClosePopup();
    // Preserve all fields including dates
    const eventToEdit = {
      ...event,
      title: event.title || '',
      description: event.description || '',
      price: event.price || '',
      hyperlink: event.hyperlink || '',
      author: event.author || '',
      // Preserve dates based on format
      eventType: event.eventType || event.dateType || 'public',
      date: event.date || event.firstDate || event.earlyDate || event.publicDate || '',
      firstDate: event.firstDate || '',
      earlyDate: event.earlyDate || '',
      publicDate: event.publicDate || '',
    };
    setEditingEvent(eventToEdit);
    setShowEditForm(true);
    setEditImage(null);
  };

  const handleEditFromPopup = () => {
    if (selectedEvent) {
      handleEdit(selectedEvent);
    }
  };

  const handleExportFromPopup = () => {
    if (!selectedEvent) return;
    
    // Extract event details
    const eventDetails = {
      title: selectedEvent.title,
      description: selectedEvent.description || '',
      author: selectedEvent.author || '',
      price: selectedEvent.price || '',
      hyperlink: selectedEvent.hyperlink || '',
      dateType: selectedEvent.dateType || selectedEvent.eventType || 'public',
      date: selectedEvent.date || selectedEvent.firstDate || selectedEvent.earlyDate || selectedEvent.publicDate || ''
    };
    
    // Create a JSON string
    const jsonString = JSON.stringify(eventDetails, null, 2);
    
    // Create a blob and download
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${selectedEvent.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_event.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      
      // Handle both new format (eventType + date) and legacy format (firstDate/earlyDate/publicDate)
      if (editingEvent.eventType && editingEvent.date !== undefined) {
        // New format: single event with eventType and date
        formData.append('title', editingEvent.title || '');
        formData.append('description', editingEvent.description || '');
        formData.append('eventType', editingEvent.eventType);
        // date might be datetime-local string or ISO string - backend will handle conversion
        formData.append('date', editingEvent.date || '');
        formData.append('price', editingEvent.price || '');
        formData.append('hyperlink', editingEvent.hyperlink || '');
        if (editingEvent.author) {
          formData.append('author', editingEvent.author);
        }
      } else {
        // Legacy format: all three dates
        formData.append('title', editingEvent.title || '');
        formData.append('description', editingEvent.description || '');
        formData.append('firstDate', editingEvent.firstDate || '');
        formData.append('earlyDate', editingEvent.earlyDate || '');
        formData.append('publicDate', editingEvent.publicDate || '');
        formData.append('price', editingEvent.price || '');
        formData.append('hyperlink', editingEvent.hyperlink || '');
        if (editingEvent.author) {
          formData.append('author', editingEvent.author);
        }
      }

      if (editImage) {
        formData.append('image', editImage);
      }

      await api.put(`/events/${editingEvent._id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setShowEditForm(false);
      setEditingEvent(null);
      setEditImage(null);
      fetchEvents();
    } catch (error) {
      console.error('Error updating event:', error);
      alert('Error updating event: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDelete = async () => {
    if (!selectedEvent || !selectedEvent._id) return;
    if (!window.confirm('Are you sure you want to delete this event?')) {
      return;
    }
    try {
      await api.delete(`/events/${selectedEvent._id}`);
      handleClosePopup();
      fetchEvents();
    } catch (error) {
      alert('Error deleting event');
    }
  };

  // Calculate month view data
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
  const monthDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  if (loading) {
    return <div className="loading">Loading calendar...</div>;
  }

  return (
    <div className="public-calendar">
      <header className="calendar-header">
        <div className="header-buttons">
          <button className="nav-button" onClick={() => viewMode === 'month' ? navigateMonth(-1) : navigateWeek(-1)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
            </svg>
          </button>
          <button className="nav-button today-button" onClick={() => setCurrentDate(new Date())}>
            Today
          </button>
          <button className="nav-button" onClick={() => viewMode === 'month' ? navigateMonth(1) : navigateWeek(1)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
            </svg>
          </button>
          </div>
        <div className="current-date-display">
          {viewMode === 'month' 
            ? format(currentDate, 'MMMM yyyy')
            : `${format(weekStart, 'MMM d')} - ${format(addDays(weekStart, 6), 'MMM d, yyyy')}`
          }
        </div>
        <div className="calendar-header-right" ref={searchWrapRef}>
          <div className="calendar-search">
            <input
              type="search"
              className="calendar-search-input"
              placeholder="Search book box, book, author…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  runCalendarSearch();
                }
              }}
              aria-label="Search events"
            />
            <button
              type="button"
              className="calendar-search-submit"
              onClick={runCalendarSearch}
              aria-label="Run search"
              title="Search"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
              </svg>
            </button>
            {searchOpen && (
              <div className="calendar-search-dropdown" role="listbox" aria-label="Search results">
                {searchResults.length === 0 ? (
                  <div className="calendar-search-empty">No events found.</div>
                ) : (
                  searchResults.map((row) => {
                    const key = `${row.event._id}-${row.dateType}-${row.date.getTime()}`;
                    return (
                      <button
                        key={key}
                        type="button"
                        className="calendar-search-result"
                        onClick={() => handleSearchResultPick(row)}
                        role="option"
                      >
                        <span className="calendar-search-result-bookbox">{row.bookBox || '—'}</span>
                        <span className="calendar-search-result-detail">
                          {row.bookName || '—'}
                          <span className="calendar-search-result-sep"> · </span>
                          {utcNyFormatDateTimeEtLabel(row.date)}
                          <span className="calendar-search-result-sep"> · </span>
                          {getDateTypeLabel(row.dateType)}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>
          <div className="view-toggle">
            <button 
              className={`view-button ${viewMode === 'month' ? 'active' : ''}`}
              onClick={() => setViewMode('month')}
            >
              Month
            </button>
            <button 
              className={`view-button ${viewMode === 'week' ? 'active' : ''}`}
              onClick={() => setViewMode('week')}
            >
              Week
            </button>
          </div>
        </div>
      </header>

      {viewMode === 'month' ? (
        <div className="month-view">
          <div className="month-grid">
            <div className="month-days">
              {monthDays.map((day, index) => {
                const dayEvents = getEventsForDay(day);
                const isCurrentMonth = isSameMonth(day, currentDate);
                const isToday = isSameDay(day, new Date());
                const dayOfWeek = format(day, 'EEE');
                return (
                  <div 
                    key={index} 
                    className={`month-day ${!isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''}`}
                  >
                    <div className="day-header-content">
                      <div className="day-name">{dayOfWeek}</div>
                      <div className="day-number">{format(day, 'd')}</div>
                    </div>
                    <div className="day-events">
                      {dayEvents.slice(0, 3).map((event, eventIndex) => {
                        const dateType = event.dateType || 'public';
                        const dateTypeLabel = dateType === 'first' ? 'First Access' : 
                                              dateType === 'early' ? 'Early Access' : 
                                              dateType === 'openPreorder' ? 'Open Preorder' :
                                              dateType === 'preorderClose' ? 'Preorder Close' :
                                              dateType === 'membersOnly' ? 'Members only' :
                                              'General Access';
                        const dateTypeClass = `event-${dateType}`;
                        const eventColor = getEventColor({ ...event, dateType });
                        return (
                          <div
                            key={`${event._id}-${dateType}-${eventIndex}`}
                            className={`month-event ${dateTypeClass}`}
                            style={{
                              '--event-color': eventColor,
                            }}
                            onClick={() => handleEventClick({ ...event, dateType, dateTypeLabel })}
                          >
                            <div className="event-content">
                              <span className="event-time">
                                {formatEstTimeFromUtcDate(event.date instanceof Date ? event.date : parseISO(event.date || event.publicDate))}
                              </span>
                              <span className="event-title-text">{event.title}</span>
                            </div>
                          </div>
                        );
                      })}
                      {dayEvents.length > 3 && (
                        <div 
                          className="more-events" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCellEventsClick(day);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          +{dayEvents.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="week-view">
          <div className="calendar-grid">
            <div className="time-column">
              <div className="day-header"></div>
              {hours.map(hour => (
                <div key={hour} className="time-slot">
                  {formatHourLabel(hour)}
                </div>
              ))}
            </div>

            {weekDays.map((day, dayIndex) => {
              const isToday = isSameDay(day, new Date());
              return (
                <div key={dayIndex} className="day-column">
                  <div className={`day-header ${isToday ? 'today' : ''}`}>
                    <div className="day-name">{format(day, 'EEE')}</div>
                    <div className={`day-number ${isToday ? 'today-number' : ''}`}>{format(day, 'd')}</div>
                  </div>
                  {hours.map(hour => {
                    const hourEvents = getEventsForDayAndHour(day, hour);
                    const maxVisibleEvents = 3;
                    const visibleEvents = hourEvents.slice(0, maxVisibleEvents);
                    const remainingCount = hourEvents.length - maxVisibleEvents;
                    const bandPct = 100 / Math.min(Math.max(visibleEvents.length, 1), maxVisibleEvents);

                    return (
                      <div key={hour} className="hour-cell">
                        {visibleEvents.map((event, eventIndex) => {
                          const dateType = event.dateType || 'public';
                          const dateTypeLabel = dateType === 'first' ? 'First Access' : 
                                                dateType === 'early' ? 'Early Access' : 
                                                dateType === 'openPreorder' ? 'Open Preorder' :
                                                dateType === 'preorderClose' ? 'Preorder Close' :
                                                dateType === 'membersOnly' ? 'Members only' :
                                                'General Access';
                          const dateTypeClass = `event-${dateType}`;
                          const eventColor = getEventColor({ ...event, dateType });
                          
                          return (
                            <div
                              key={`${event._id}-${dateType}-${eventIndex}`}
                              className={`event-block ${dateTypeClass}`}
                              style={{
                                height: `${bandPct}%`,
                                top: `${eventIndex * bandPct}%`,
                                '--event-color': eventColor,
                              }}
                              title={`${event.title} - ${dateTypeLabel}${event.price ? ` - ${event.price}` : ''}`}
                              onClick={() => handleEventClick({ ...event, dateType, dateTypeLabel })}
                            >
                              <div className="event-content">
                                <div className="event-title">{event.title}</div>
                                <div className="event-date-type">{dateTypeLabel}</div>
                                {event.price && event.price.trim() !== '' && (
                                  <div className="event-price">{event.price}</div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        {remainingCount > 0 && (
                          <div 
                            className="more-events" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCellEventsClick(day, hour);
                            }}
                            style={{ cursor: 'pointer', position: 'absolute', bottom: '2px', left: '2px', right: '2px', fontSize: '11px', padding: '2px 4px', backgroundColor: 'rgba(255,255,255,0.9)', borderRadius: '2px' }}
                          >
                            +{remainingCount} more
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showEventPopup && selectedEvent && (() => {
        const relatedEvents = findRelatedEvents(selectedEvent);
        const { bookBox: popupBookBox } = parseEventTitle(selectedEvent.title);
        const currentDateType = selectedEvent.dateType || selectedEvent.eventType || 'public';
        
        // Get current event date based on dateType
        let currentEventDate = null;
        if (selectedEvent.date) {
          currentEventDate = selectedEvent.date instanceof Date ? selectedEvent.date : parseISO(selectedEvent.date);
        } else if (currentDateType === 'first' && selectedEvent.firstDate) {
          currentEventDate = selectedEvent.firstDate instanceof Date ? selectedEvent.firstDate : parseISO(selectedEvent.firstDate);
        } else if (currentDateType === 'early' && selectedEvent.earlyDate) {
          currentEventDate = selectedEvent.earlyDate instanceof Date ? selectedEvent.earlyDate : parseISO(selectedEvent.earlyDate);
        } else if (currentDateType === 'public' && selectedEvent.publicDate) {
          currentEventDate = selectedEvent.publicDate instanceof Date ? selectedEvent.publicDate : parseISO(selectedEvent.publicDate);
        } else {
          const fallbackDate = selectedEvent.firstDate || selectedEvent.earlyDate || selectedEvent.publicDate;
          currentEventDate = fallbackDate ? (fallbackDate instanceof Date ? fallbackDate : parseISO(fallbackDate)) : null;
        }
        
        // Format date/time like Google Calendar: "Thursday, January 8·5:00 – 6:00am"
        let dateTimeDisplay = '';
        if (currentEventDate && !isNaN(currentEventDate.getTime())) {
          const dayName = format(currentEventDate, 'EEEE, MMMM d');
          const startTime = toTightAmPm(currentEventDate);
          const endTime = toTightAmPm(new Date(currentEventDate.getTime() + 60 * 60 * 1000));
          dateTimeDisplay = `${dayName}·${startTime} – ${endTime}`;
        }
        
        return (
          <div className="event-popup-overlay" onClick={handleClosePopup}>
            <div className="event-popup" onClick={(e) => e.stopPropagation()}>
              <div className="event-popup-wrapper">
                <div className="event-popup-top-actions">
                  {isLoggedIn && (
                    <>
                      <button 
                        className="event-popup-icon-btn" 
                        onClick={handleEditFromPopup} 
                        aria-label="Edit"
                        title="Edit"
                      >
                        <svg focusable="false" width="20" height="20" viewBox="0 0 24 24">
                          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"></path>
                        </svg>
                      </button>
                      <button 
                        className="event-popup-icon-btn" 
                        onClick={handleDelete} 
                        aria-label="Delete"
                        title="Delete"
                      >
                        <svg focusable="false" width="20" height="20" viewBox="0 0 24 24">
                          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"></path>
                        </svg>
                      </button>
                    </>
                  )}
                  <button className="event-popup-close" onClick={handleClosePopup} aria-label="Close">
                    <svg focusable="false" width="20" height="20" viewBox="0 0 24 24">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"></path>
                    </svg>
                  </button>
                </div>
                
                <div className="event-popup-content-wrapper" style={{ display: 'flex', flexDirection: 'column' }}>
                  <div className="event-popup-color-bar" style={{ backgroundColor: getEventColor(selectedEvent) }}></div>
                  
                  <div className="event-popup-content" style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <div className="event-popup-header" style={{ display: 'flex', flexDirection: 'column', padding: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <div className="event-popup-color-square" style={{ backgroundColor: getEventColor(selectedEvent) }}></div>
                        <h1 className="event-popup-title" style={{ marginLeft: '8px', marginBottom: 0 }}>{selectedEvent.title}</h1>
                      </div>
                      {dateTimeDisplay && (
                        <div className="event-popup-datetime">{dateTimeDisplay}</div>
                      )}
                    </div>
                    
                    <div className="event-popup-notes" style={{ padding: '0 16px 8px', display: 'flex', flexDirection: 'column' }}>
                      {(() => {
                        // Get related dates
                        let earlyAccessDate = null;
                        let generalAccessDate = null;
                        let preorderCloseDate = null;
                        
                        if (relatedEvents.earlyAccess) {
                          earlyAccessDate = relatedEvents.earlyAccess.date || relatedEvents.earlyAccess.earlyDate;
                        } else if (currentDateType === 'first' && selectedEvent.earlyDate) {
                          earlyAccessDate = selectedEvent.earlyDate instanceof Date ? selectedEvent.earlyDate : parseISO(selectedEvent.earlyDate);
                        } else if (currentDateType === 'early' && currentEventDate) {
                          earlyAccessDate = currentEventDate;
                        }
                        
                        if (relatedEvents.generalAccess) {
                          generalAccessDate = relatedEvents.generalAccess.date || relatedEvents.generalAccess.publicDate;
                        } else if ((currentDateType === 'first' || currentDateType === 'early') && selectedEvent.publicDate) {
                          generalAccessDate = selectedEvent.publicDate instanceof Date ? selectedEvent.publicDate : parseISO(selectedEvent.publicDate);
                        } else if (currentDateType === 'public' && currentEventDate) {
                          generalAccessDate = currentEventDate;
                        }

                        if (relatedEvents.preorderClose) {
                          preorderCloseDate = relatedEvents.preorderClose.date || relatedEvents.preorderClose.preorderCloseDate;
                        } else if (currentDateType === 'openPreorder' && selectedEvent.preorderCloseDate) {
                          preorderCloseDate =
                            selectedEvent.preorderCloseDate instanceof Date
                              ? selectedEvent.preorderCloseDate
                              : parseISO(selectedEvent.preorderCloseDate);
                        }
                        
                        return (
                          <>
                            {/* FIRST ACCESS + EARLY ACCESS (public calendar) */}
                      {((currentDateType === 'first' || currentDateType === 'early') && (earlyAccessDate || currentDateType === 'early')) && (() => {
                        const sortByTimeStr = (a, b) => {
                          const parseTime = (timeStr) => {
                            const match = timeStr.match(/(\d+)(?::(\d+))?(am|pm)/i);
                            if (!match) return 0;
                            let hours = parseInt(match[1], 10);
                            const period = match[3].toLowerCase();
                            if (period === 'pm' && hours !== 12) hours += 12;
                            if (period === 'am' && hours === 12) hours = 0;
                            return hours;
                          };
                          return parseTime(a.time) - parseTime(b.time);
                        };

                        const allFirstFromApi = getAllFirstAccessTimes(selectedEvent);
                        const allEarlyFromApi = getAllEarlyAccessTimes(selectedEvent);
                        const legacyFirstTimes = [];
                        const legacyEarlyTimes = [];
                        if (allEarlyFromApi.length === 0) {
                          if (earlyAccessDate && currentDateType === 'first') {
                            const estTime = formatTimeSimple(earlyAccessDate);
                            const gmtTime = estTime ? utcToTightLondon(earlyAccessDate) : null;
                            const gmtZone = estTime ? utcLondonZoneAbbr(earlyAccessDate) : null;
                            let region = 'UK/INT';
                            if (currentEventDate) {
                              const currentTime = formatTimeSimple(currentEventDate);
                              const earlyTime = formatTimeSimple(earlyAccessDate);
                              if (currentTime && earlyTime) {
                                const parseT = (timeStr) => {
                                  const match = timeStr.match(/(\d+)(?::(\d+))?(am|pm)/i);
                                  if (!match) return 0;
                                  let hours = parseInt(match[1], 10);
                                  const period = match[3].toLowerCase();
                                  if (period === 'pm' && hours !== 12) hours += 12;
                                  if (period === 'am' && hours === 12) hours = 0;
                                  return hours;
                                };
                                region = parseT(earlyTime) > parseT(currentTime) ? 'US/Canada' : 'UK/INT';
                              }
                            }
                            if (estTime) {
                              legacyFirstTimes.push({ time: estTime, gmtTime, gmtZone, region });
                            }
                          }
                          if (currentDateType === 'early' && currentEventDate) {
                            const estTime = formatTimeSimple(currentEventDate);
                            const gmtTime = estTime ? utcToTightLondon(currentEventDate) : null;
                            const gmtZone = estTime ? utcLondonZoneAbbr(currentEventDate) : null;
                            if (estTime) {
                              let region = inferEarlySubscriberRegionFromEvent(selectedEvent);
                              if (!region) {
                                const d = currentEventDate instanceof Date ? currentEventDate : parseISO(currentEventDate);
                                region =
                                  d && !isNaN(d.getTime()) && nyClockHour(d) < 12 ? 'UK/INT' : 'US/Canada';
                              }
                              legacyEarlyTimes.push({ time: estTime, gmtTime, gmtZone, region });
                            }
                          }
                        } else {
                          if (earlyAccessDate && currentDateType === 'first') {
                            const estTime = formatTimeSimple(earlyAccessDate);
                            const gmtTime = estTime ? utcToTightLondon(earlyAccessDate) : null;
                            const gmtZone = estTime ? utcLondonZoneAbbr(earlyAccessDate) : null;
                            let region = 'UK/INT';
                            if (currentEventDate) {
                              const currentTime = formatTimeSimple(currentEventDate);
                              const earlyTime = formatTimeSimple(earlyAccessDate);
                              if (currentTime && earlyTime) {
                                const parseT = (timeStr) => {
                                  const match = timeStr.match(/(\d+)(?::(\d+))?(am|pm)/i);
                                  if (!match) return 0;
                                  let hours = parseInt(match[1], 10);
                                  const period = match[3].toLowerCase();
                                  if (period === 'pm' && hours !== 12) hours += 12;
                                  if (period === 'am' && hours === 12) hours = 0;
                                  return hours;
                                };
                                region = parseT(earlyTime) > parseT(currentTime) ? 'US/Canada' : 'UK/INT';
                              }
                            }
                            if (estTime) {
                              legacyFirstTimes.push({ time: estTime, gmtTime, gmtZone, region });
                            }
                          }
                        }

                        const firstRows = [...allFirstFromApi, ...legacyFirstTimes].sort(sortByTimeStr);
                        const earlyRows = [...allEarlyFromApi, ...(allEarlyFromApi.length === 0 ? legacyEarlyTimes : [])].sort(
                          sortByTimeStr
                        );

                        const firstHeaderDateInput =
                          firstRows.length > 0
                            ? (currentDateType === 'first' && selectedEvent.firstDate
                                ? (selectedEvent.firstDate instanceof Date
                                    ? selectedEvent.firstDate
                                    : parseISO(selectedEvent.firstDate))
                                : null) || earlyAccessDate
                            : null;
                        const firstHeaderFormatted = firstHeaderDateInput
                          ? formatDateWithOrdinal(firstHeaderDateInput)
                          : null;
                        const earlyHeaderFormatted = earlyRows.length > 0 && earlyAccessDate
                          ? formatDateWithOrdinal(earlyAccessDate)
                          : null;

                        if (firstRows.length === 0 && earlyRows.length === 0) return null;

                        return (
                          <>
                            {firstRows.length > 0 && (
                              <div style={{ marginBottom: '16px' }}>
                                <div>
                                  FIRST ACCESS
                                  {currentDateType !== 'first' && firstHeaderFormatted && (
                                    <>
                                      {': '}<strong>{firstHeaderFormatted}</strong>
                                    </>
                                  )}
                                </div>
                                <br />
                                <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                                  {firstRows.map((timeInfo, index) => (
                                    <li key={`f-${index}`}>
                                      <strong>
                                        {timeInfo.time} ET
                                        {fairylootEtRegionalSuffix(timeInfo, popupBookBox)}
                                      </strong>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {earlyRows.length > 0 && (
                              <div style={{ marginBottom: '16px' }}>
                                <div>
                                  EARLY ACCESS
                                  {currentDateType !== 'early' && earlyHeaderFormatted && (
                                    <>
                                      {': '}<strong>{earlyHeaderFormatted}</strong>
                                    </>
                                  )}
                                </div>
                                <br />
                                <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                                  {earlyRows.map((timeInfo, index) => (
                                    <li key={`e-${index}`}>
                                      <strong>
                                        {timeInfo.time} ET
                                        {fairylootEtRegionalSuffix(timeInfo, popupBookBox)}
                                      </strong>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </>
                        );
                      })()}

                      {/* GENERAL ACCESS section */}
                      {((currentDateType === 'first' || currentDateType === 'early' || currentDateType === 'public') && (generalAccessDate || currentDateType === 'public')) && (() => {
                        const allGeneralAccessTimes = getAllGeneralAccessTimes(selectedEvent, relatedEvents);
                        const generalDate = generalAccessDate || (currentDateType === 'public' ? currentEventDate : null);
                        const formattedDate = generalDate ? formatDateWithOrdinal(generalDate) : null;
                        
                        // Collect all general access times
                        const generalTimes = [];
                        
                        if (allGeneralAccessTimes.length > 0) {
                          allGeneralAccessTimes.forEach(timeInfo => {
                            generalTimes.push({
                              time: timeInfo.time,
                              gmtTime: timeInfo.gmtTime,
                              gmtZone: timeInfo.gmtZone,
                              region: timeInfo.region || 'Worldwide'
                            });
                          });
                        } else if (generalDate) {
                          const estTime = formatTimeSimple(generalDate);
                          const gmtTime = estTime ? utcToTightLondon(generalDate) : null;
                          const gmtZone = estTime ? utcLondonZoneAbbr(generalDate) : null;
                          if (estTime) {
                            generalTimes.push({
                              time: estTime,
                              gmtTime: gmtTime,
                              gmtZone,
                              region: 'Worldwide'
                            });
                          }
                        }
                        
                        // Sort by time
                        generalTimes.sort((a, b) => {
                          const parseTime = (timeStr) => {
                            const match = timeStr.match(/(\d+)(?::(\d+))?(am|pm)/i);
                            if (!match) return 0;
                            let hours = parseInt(match[1]);
                            const period = match[3].toLowerCase();
                            if (period === 'pm' && hours !== 12) hours += 12;
                            if (period === 'am' && hours === 12) hours = 0;
                            return hours;
                          };
                          return parseTime(a.time) - parseTime(b.time);
                        });
                        
                        if (formattedDate || generalTimes.length > 0) {
                          return (
                            <div style={{ marginBottom: '16px' }}>
                              <div>
                                GENERAL ACCESS
                                {currentDateType !== 'public' && formattedDate && (
                                  <>
                                    {': '}<strong>{formattedDate}</strong>
                                  </>
                                )}
                              </div>
                              {generalTimes.length > 0 && (
                                <>
                                  <br />
                                  <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                                    {generalTimes.map((timeInfo, index) => (
                                        <li key={index}>
                                          <strong>
                                            {timeInfo.time} ET
                                            {fairylootEtRegionalSuffix(timeInfo, popupBookBox)}
                                          </strong>
                                        </li>
                                      ))}
                                  </ul>
                                </>
                              )}
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {currentDateType === 'openPreorder' && preorderCloseDate && (
                        <div style={{ marginBottom: '16px' }}>
                          PREORDER CLOSE
                          <br />
                          <strong>{formatDateWithOrdinal(preorderCloseDate)}</strong>
                        </div>
                      )}

                      {/* Price */}
                      {selectedEvent.price && selectedEvent.price.trim() !== '' && (
                        <div style={{ marginBottom: '16px' }}>
                          Price: <strong>{selectedEvent.price}</strong>
                        </div>
                      )}

                      {/* Estimated to ship */}
                      {(() => {
                        const shipmentDate = extractShipmentDate(selectedEvent.description || '');
                        const linkUrl = extractLinkFromDescription(selectedEvent.description || '') || selectedEvent.hyperlink;
                        if (shipmentDate || linkUrl) {
                          return (
                            <div style={{ marginBottom: '16px' }}>
                              {shipmentDate && (
                                <>
                                  Estimated to ship: <strong>{shipmentDate}</strong>
                                </>
                              )}
                              {linkUrl && (
                                <div style={{ marginTop: shipmentDate ? '8px' : '0' }}>
                                  <a href={linkUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1a73e8', textDecoration: 'none' }}>
                                    LINK
                                  </a>
                                  <br />
                                  <div>@shadowkissedchronicles Book Box Preorder Calendar</div>
                                </div>
                              )}
                            </div>
                          );
                        }
                        return null;
                      })()}
                          </>
                        );
                      })()}
                    </div>
                    </div>
                    {/* Cover image thumbnail */}
                    {hasRealImage(selectedEvent.image) && (
                      <div style={{ flexShrink: 0, width: '90px', padding: '16px 16px 0 0' }}>
                        <img
                          src={getImageUrl(selectedEvent.image)}
                          alt="Book cover"
                          onError={(e) => {
                            // Hide broken/missing cover URLs to avoid the browser's default missing-image icon.
                            e.currentTarget.style.display = 'none';
                          }}
                          style={{ width: '90px', borderRadius: '6px', display: 'block', objectFit: 'cover' }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {showCellEventsPopup && cellEvents.length > 0 && cellDay && (
        <div className="event-popup-overlay" onClick={handleCloseCellEventsPopup}>
          <div className="event-popup" onClick={(e) => e.stopPropagation()}>
            <div className="event-popup-wrapper">
              <div className="event-popup-top-actions">
                <button className="event-popup-close" onClick={handleCloseCellEventsPopup} aria-label="Close">
                  <svg focusable="false" width="20" height="20" viewBox="0 0 24 24">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"></path>
                  </svg>
                </button>
              </div>
              
              <div className="event-popup-content-wrapper" style={{ display: 'flex', flexDirection: 'column' }}>
                <div className="event-popup-content" style={{ display: 'flex', flexDirection: 'column', padding: '16px' }}>
                  <h2 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '500' }}>
                    {cellHour !== null && cellHour !== undefined
                      ? `Events on ${format(cellDay, 'EEEE, MMMM d')} at ${formatHourLabel(cellHour)}`
                      : `Events on ${format(cellDay, 'EEEE, MMMM d')}`
                    }
                  </h2>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {cellEvents.map((event, index) => {
                      const dateType = event.dateType || 'public';
                      const dateTypeLabel = dateType === 'first' ? 'First Access' : 
                                            dateType === 'early' ? 'Early Access' : 
                                            dateType === 'openPreorder' ? 'Open Preorder' :
                                            dateType === 'preorderClose' ? 'Preorder Close' :
                                            dateType === 'membersOnly' ? 'Members only' :
                                            'General Access';
                      const eventColor = getEventColor({ ...event, dateType });
                      const eventDate = event.date || parseISO(event.publicDate || event.firstDate || event.earlyDate);
                      
                      return (
                        <div
                          key={`${event._id}-${dateType}-${index}`}
                          onClick={() => {
                            handleCloseCellEventsPopup();
                            handleEventClick({ ...event, dateType, dateTypeLabel });
                          }}
                          style={{
                            padding: '12px',
                            border: '1px solid #e8eaed',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <div 
                            style={{ 
                              width: '12px', 
                              height: '12px', 
                              borderRadius: '2px', 
                              backgroundColor: eventColor,
                              flexShrink: 0
                            }} 
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '500', marginBottom: '4px' }}>{event.title}</div>
                            <div style={{ fontSize: '12px', color: '#5f6368' }}>
                              {formatEstTimeFromUtcDate(eventDate instanceof Date ? eventDate : parseISO(eventDate))} · {dateTypeLabel}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditForm && editingEvent && (
        <div className="edit-modal">
          <div className="edit-modal-content">
            <h3>Edit Event</h3>
            <form onSubmit={handleSaveEdit}>
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={editingEvent.title || ''}
                  onChange={(e) => setEditingEvent({...editingEvent, title: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <ReactQuill
                  value={editingEvent.description || ''}
                  onChange={(value) => setEditingEvent({...editingEvent, description: value})}
                  theme="snow"
                  modules={{
                    toolbar: [
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                      ['link'],
                      ['clean']
                    ],
                  }}
                />
              </div>
              
              {/* New format: eventType + date */}
              {editingEvent.eventType && editingEvent.date ? (
                <>
                  <div className="form-group">
                    <label>Event Type</label>
                    <select
                      value={editingEvent.eventType}
                      onChange={(e) => setEditingEvent({...editingEvent, eventType: e.target.value})}
                      required
                    >
                      <option value="first">First Access (For customers who purchased books)</option>
                      <option value="early">Early Access (For active subscription)</option>
                      <option value="public">Public Access (For everyone)</option>
                      <option value="openPreorder">Open Preorder</option>
                      <option value="preorderClose">Preorder Close</option>
                      <option value="membersOnly">Members Only</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Date & Time</label>
                    <input
                      type="datetime-local"
                      value={formatDateTimeLocal(editingEvent.date)}
                      onChange={(e) => setEditingEvent({...editingEvent, date: e.target.value})}
                      required
                    />
                  </div>
                </>
              ) : (
                /* Legacy format: all three dates */
                <>
                  <div className="form-group">
                    <label>First Date & Time (For customers who purchased books)</label>
                    <input
                      type="datetime-local"
                      value={formatDateTimeLocal(editingEvent.firstDate)}
                      onChange={(e) => setEditingEvent({...editingEvent, firstDate: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Early Date & Time (For active subscription)</label>
                    <input
                      type="datetime-local"
                      value={formatDateTimeLocal(editingEvent.earlyDate)}
                      onChange={(e) => setEditingEvent({...editingEvent, earlyDate: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Public Date & Time (For everyone)</label>
                    <input
                      type="datetime-local"
                      value={formatDateTimeLocal(editingEvent.publicDate)}
                      onChange={(e) => setEditingEvent({...editingEvent, publicDate: e.target.value})}
                    />
                  </div>
                </>
              )}
              
              <div className="form-group">
                <label>Price</label>
                <input
                  type="text"
                  value={editingEvent.price || ''}
                  onChange={(e) => setEditingEvent({...editingEvent, price: e.target.value})}
                  placeholder="e.g., £48 GBP / $64 USD + shipping"
                />
              </div>
              <div className="form-group">
                <label>Hyperlink</label>
                <input
                  type="url"
                  value={editingEvent.hyperlink || ''}
                  onChange={(e) => setEditingEvent({...editingEvent, hyperlink: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Author</label>
                <input
                  type="text"
                  value={editingEvent.author || ''}
                  onChange={(e) => setEditingEvent({...editingEvent, author: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Image</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {(editImage ? URL.createObjectURL(editImage) : editingEvent.image) && (
                    <img
                      src={editImage ? URL.createObjectURL(editImage) : getImageUrl(editingEvent.image)}
                      alt="Book cover"
                      style={{ width: '80px', borderRadius: '4px', objectFit: 'cover' }}
                    />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setEditImage(e.target.files[0] || null)}
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => { setShowEditForm(false); setEditImage(null); }} className="cancel-btn">
                  Cancel
                </button>
                <button type="submit" className="save-btn">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicCalendar;

